/**
 * Browse slice — Discover's Steam-backed search and tag browse
 * (docs/DISCOVERY.md §2.2, §5).
 *
 * Discover has two modes and this slice owns one of them:
 *
 *   LOCAL   nothing typed, no tags picked. The games table, ranked and paged
 *           client-side. Phase 1's behaviour, untouched.
 *   BROWSE  a term or at least one tag. Steam's own search across ~272,000
 *           games, 50 rows a page, ordered by Steam's relevance.
 *
 * `browseMode()` is the single definition of which one is live, so no screen or
 * selector has to re-derive it and get it subtly different.
 *
 * ROWS LAND IN `s.live`, DELIBERATELY. A browse row IS a live-search row — same
 * shape, same provenance, same "thin" facts — so putting it anywhere else would
 * mean `gById`, `openGame`, `detailVM`, `appidsOf`, `queueAssets` and `quickAdd`
 * each needing to learn about a second array. They do not, because there is not
 * one. `repo.fetchBrowse` registers the same rows on its side, which is what
 * keeps quick-add from writing a nameless row into the crew's games table.
 *
 * NOT EVERY ROW IN `s.live` IS `live: true`, and that is load-bearing. A term
 * browse merges what our own ecosystem already knows about the same question:
 * `discover_page` matches lead the page (the only door a delisted title has
 * left — Steam's search omits one outright), and a Steam row the catalog knows
 * is swapped for its rich twin. Those are FULL rows and they keep
 * `live: false`, because `revMeta` reads `live` as "facts not fetched yet" and
 * would suppress a real rating, and `mkBars` would drop a real 90-day chart —
 * the flag describes the ROW's provenance, not the array it sits in.
 */

import type { StateCreator } from 'zustand';

import { soloOnlyCats, tagId } from '../../lib/format.ts';
import { DISCOVER_LIMIT, defaultFilters } from '../../lib/logic.ts';
import type { FilterKey, Game, SteamFactPatch } from '../../lib/types.ts';
import type { BrowseQuery, SeatCount, SteamTag } from '../../repo/Repository.ts';
import { applyFact } from './steamFacts.ts';
import { emptyTagMap, fltKeyOf } from '../filterKey.ts';
import type { QdStore } from '../types.ts';

/** Steam pages this endpoint at 50; the client mirrors it so `start` lines up. */
export const BROWSE_PAGE = 50;

/** Typing debounce. Matches the live-search debounce so the two feel identical. */
export const BROWSE_DEBOUNCE_MS = 380;

/**
 * The tags offered before you search. Names, not ids: ids are resolved against
 * the live vocabulary at render time, so a Steam re-numbering shows a shorter
 * list rather than silently filtering by the wrong thing.
 */
export const POPULAR_TAG_NAMES: readonly string[] = [
  'Co-op',
  'Online Co-Op',
  'Multiplayer',
  'PvP',
  'Horror',
  'Survival',
  'Shooter',
  'FPS',
  'RPG',
  'Action',
  'Adventure',
  'Strategy',
  'Roguelike',
  'Simulation',
  'Puzzle',
  'Platformer',
  'Racing',
  'Sports',
  'Story Rich',
  'Open World',
  'Sandbox',
  'Indie',
  'Casual',
  'Party',
];

export interface BrowseSlice {
  /**
   * INCLUDED Steam tag ids, PER SCREEN (`store/filterKey.ts`) — the tag lists
   * are filter state and were separated with the rest of it on 2026-08-27.
   *
   * Only `bTagsBy.discover` is the browse QUESTION, the one Steam ANDs and the
   * one `browseMode` reads; every other key is a local predicate over rows
   * already in hand (`selectors.ts#tagLensOf`). Everything in this file is
   * Discover's pipeline, so every read here is pinned to `.discover` — a fetch
   * can land after a nav, and "whatever screen we are on now" would be the
   * wrong answer exactly then.
   */
  bTagsBy: Record<FilterKey, number[]>;
  /**
   * EXCLUDED Steam tag ids, per screen. Never part of the browse question: an
   * exclude narrows whatever mode is already showing — the feed server-side
   * (sent to `discover_page` as `xtags` slugs, honest whole-set totals) and a
   * browse client-side (`discoverVM`'s predicate over `Game.tids`) — and never
   * flips the mode, which is why `browseMode()` does not read it. Mutually
   * exclusive with the include list per id: `cycleTag` moves an id between them
   * atomically, so no tag is ever simultaneously required and forbidden.
   */
  xTagsBy: Record<FilterKey, number[]>;
  /**
   * Steam's count of everything matching PLUS `bLocalN` — NOT the number of
   * rows loaded. The two travel together so the match line reports one number
   * for one merged result set (DISCOVERY.md §5.5).
   */
  bTotal: number;
  /**
   * How many local-catalog matches page 0 merged that Steam's page did not
   * return. Counted when the query's first page lands, carried through LOAD
   * MORE so re-deriving `bTotal` from a later page's total does not silently
   * drop the extras — and DECREMENTED when a later page proves Steam counts
   * one of them after all. Always `bLocalExtras.length`.
   */
  bLocalN: number;
  /**
   * The appids behind `bLocalN`. Page 0 can only compare its extras against
   * page 0's Steam rows, so an extra Steam ranks below the first page is
   * counted on BOTH sides of `bTotal = page.total + bLocalN`. When a later
   * page delivers Steam's copy, the dedupe drops the duplicate row — and this
   * set is how the COUNT drops with it. Without it the overcount is permanent:
   * after the last real page `remaining` stays > 0 and LOAD MORE becomes an
   * immortal button fetching empty pages (the worker answers an out-of-range
   * `start` with the full total and no rows).
   */
  bLocalExtras: number[];
  /** Offset the next page starts at. */
  bStart: number;
  /** Reached Steam's paging wall rather than the end of the results. */
  bCapped: boolean;
  bState: '' | 'busy' | 'ok' | 'err';
  /** How many rows the current browse has loaded, across all pages so far. */
  bLoaded: number;

  /** Steam's 446-row tag vocabulary. Empty until `loadTagVocab` lands. */
  tagVocab: SteamTag[];
  /** The picker's own search box — NOT a filter, just a way through 446 rows. */
  tagQ: string;

  /** One picker tap: off → include (search) → exclude → off. */
  cycleTag: (id: number) => void;
  /** A chip-rail ✕: drop the tag entirely, from whichever list holds it. */
  removeTag: (id: number) => void;
  /** Clears BOTH lists — includes and excludes. */
  clearTags: () => void;
  setTagQ: (v: string) => void;
  loadTagVocab: () => Promise<void>;
  /** Fetch page 0, or append the next page with `{ more: true }`. */
  fetchBrowse: (opts?: { more?: boolean }) => Promise<void>;
  /**
   * THE SEAT HEAL (migration 3034) — the live-once lookup, from the client
   * side. Called by `fetchBrowse` with every rendered appid whose row could
   * not answer the seat question; answers pop onto the tiles the way the
   * price heal's do, and the crew lens starts counting them the same render.
   *
   * Fire-and-forget by design: a browse never waits on it, a failure changes
   * nothing on screen, and a row it patches is only ever a row that knew
   * NOTHING — a count from the swap (v_games' resolved answer, overrides and
   * editorial included) is never overwritten by this path.
   */
  lookupSeats: (appids: number[]) => Promise<void>;
  /**
   * Re-ask whichever of Discover's pipelines is live, for a NEW SORT OR FILTER.
   *
   * The one entry point every sort and filter setter calls, and the reason it
   * exists is that they all used to call `restartFeed()` unconditionally — which
   * returns immediately in browse mode. So with a tag active, changing the sort
   * or a filter reached neither pipeline: the feed refused the request because
   * browse owned the screen, and the browse never heard about it. The page in
   * hand simply stayed, under its new heading.
   *
   * `prev` is the state as it was BEFORE the setter's mutation, which is what
   * lets browse mode tell a change Steam cares about from one it does not. The
   * caller has to snapshot it because only the caller is on the right side of
   * its own `set()`; deriving it from the last fetch instead would make the
   * decision depend on how the current rows happened to arrive.
   */
  restartDiscover: (prev: Pick<QdStore, 'q' | 'bTagsBy' | 'sort' | 'fltBy'>) => void;
}

/**
 * Is Discover in browse mode? One definition, used by the slice, the selectors
 * and both screens.
 */
export function browseMode(s: Pick<QdStore, 'q' | 'bTagsBy'>): boolean {
  return s.q.trim() !== '' || s.bTagsBy.discover.length > 0;
}

/**
 * Our sort and filters in STEAM'S vocabulary — the half of the question Steam
 * can answer over the whole result set rather than over the pages we hold.
 *
 * WHY ANY OF THIS GOES UPSTREAM. Discover holds 50 rows of a 20,689-match tag
 * browse. Sorting or filtering what it holds answers a question about page 1
 * while wearing the label of a question about the result set — and worse, the
 * three filters that could not read a thin Steam row (`mx: 0`, `p: null`,
 * release state read as unknown) answered it by silently changing nothing at
 * all. Steam can answer the real question; `worker/src/steam.ts#BROWSE_SORTS`
 * and `#BrowseNarrow` carry the measurements of exactly which parts.
 *
 * WHAT IS NOT HERE, and is named on screen instead of being faked:
 *
 *   - `peak` — Steam has NO player-count sort. `Players_DESC` and
 *     `Concurrent_DESC` both return the unsorted baseline. It maps to `null`,
 *     Steam's own popularity-shaped default order stands in, and `discoverVM`
 *     says so. `Reviews_DESC` is NOT substituted: a review count is a
 *     popularity proxy, not a player count.
 *   - The price range's LOW bound — Steam's `minprice` is ignored (measured),
 *     and a browse row carries no price to apply it to downstream either.
 *   - The crew lens `f.p` — "N+ players" is not a question Steam's search can
 *     be asked, and `mx` is the 0 sentinel on every browse row.
 *
 * The demo tri-state and the EA/released tri-states stay CLIENT-side over the
 * loaded pages: Steam has no demo filter (`demos=1` is ignored), and release
 * state is now readable on the row itself (`Game.stOk`), so `passFlt` answers
 * both honestly under the `· OF N LOADED` scope the match line already states.
 */
export function browseNarrow(
  s: Pick<QdStore, 'sort' | 'fltBy'>,
): Pick<BrowseQuery, 'sort' | 'maxPrice' | 'specials' | 'comingSoon'> {
  // Discover's own set, named rather than inferred — this runs inside an async
  // fetch that can resolve on another screen.
  const flt = s.fltBy.discover;
  const sort: BrowseQuery['sort'] =
    s.sort === 'new'
      ? 'Released_DESC'
      : s.sort === 'rating'
        ? 'Reviews_DESC'
        : s.sort === 'priceAsc'
          ? 'Price_ASC'
          : // 'rel' is Steam's default and is expressed by omission; 'peak' has no
            // Steam equivalent and falls back to the same default.
            null;

  // `pr[1] === 60` is the slider's OFF position ("$60+"), not a $60 cap. Only a
  // cap the user actually moved is forwarded, and a cap of 0 is Steam's
  // `maxprice=free` rather than a nonsensical `maxprice=0`.
  const hi = flt.pr?.[1] ?? 60;
  const maxPrice: BrowseQuery['maxPrice'] = hi >= 60 ? null : hi <= 0 ? 'free' : Math.ceil(hi);

  const st = flt.st;
  return {
    sort,
    maxPrice,
    // `disc > 1` asks for a MINIMUM percentage, which Steam's `specials` flag
    // cannot express. Forwarding it as "any sale" is a WIDENING, never a
    // narrowing, so the page still contains every row the exact question wants
    // — which is the only safe direction to be wrong in.
    //
    // `passFlt` then tightens to the requested percentage only where the row
    // carries a price, and a browse row carries none unless this session healed
    // one (`heal()` below replays `p`/`o` from `s.facts`). So the threshold is
    // genuinely partial on this surface, and `discoverVM` names it rather than
    // letting ≥50% quietly mean "on sale".
    specials: flt.disc > 0,
    // Two spellings of one question. `filter=comingsoon` is the only release
    // state Steam offers, so only the arm that wants unreleased rows ALONE can
    // be forwarded; "released only" has no upstream form and is left to
    // `passFlt`, which can now read `up` off the row.
    comingSoon: st?.unrel === 'only' || st?.rel === 'hide',
  };
}

/**
 * The identity of the current browse question, for the stale-response guard.
 *
 * `xTags` is DELIBERATELY absent: excludes are not part of the question Steam
 * was asked, only of how the answer is shown. A page that lands after the
 * excludes changed is still the right answer — the exclusion is applied at
 * render, in `discoverVM` — so discarding it would throw away a valid page to
 * refetch the identical bytes.
 *
 * THE SORT AND THE NARROWING **ARE** IN IT, for the mirror-image reason: they
 * now ride to Steam, so they change both the rows and `total`. A page fetched
 * under one of them appended under another would put one question's rows behind
 * another question's count — the §5.5 failure, arriving through a race. This is
 * the same rule `feedKey` states from the feed's side.
 */
function browseKey(s: Pick<QdStore, 'q' | 'bTagsBy' | 'sort' | 'fltBy'>): string {
  const n = browseNarrow(s);
  return (
    s.q.trim().toLowerCase() +
    '|' +
    [...s.bTagsBy.discover].sort((a, b) => a - b).join(',') +
    '|' +
    (n.sort ?? '') +
    '|' +
    (n.maxPrice ?? '') +
    '|' +
    (n.specials ? '1' : '') +
    (n.comingSoon ? 'c' : '')
  );
}

/**
 * The excludes as `discover_page` wants them: `steam_tags.slug` strings,
 * client-slugified from the picker's vocabulary names. `tagId` is
 * character-identical to the server-side slugger that generated the column
 * (`format.ts` says so at its definition), which is what lets the round trip
 * id → name → slug → id land on the tag the user actually tapped.
 *
 * An id whose vocabulary name never arrived CANNOT be slugified and is
 * dropped — silently inert server-side, but still visible and removable as a
 * `NOT: #id` chip, and still fully effective in browse mode where the
 * comparison is by id. Defensive only: ids enter `xTags` through a picker
 * whose vocabulary had loaded, so a nameless exclude takes a lost session
 * cache to construct.
 */
export function xtagSlugs(s: Pick<QdStore, 'xTagsBy' | 'tagVocab'>): string[] {
  const xTags = s.xTagsBy.discover;
  if (xTags.length === 0) return [];
  const byId = new Map(s.tagVocab.map((t) => [t.id, t.name]));
  const out: string[] = [];
  for (const id of xTags) {
    const name = byId.get(id);
    if (name !== undefined) out.push(tagId(name));
  }
  return out;
}

export const createBrowseSlice: StateCreator<QdStore, [], [], BrowseSlice> = (set, get) => {
  /** Per-store, not per-module: two stores in one process must not share it. */
  let debT: ReturnType<typeof setTimeout> | null = null;
  /**
   * Appids this SESSION has already sent to the seat lookup — per-store, like
   * `debT`. The server's igdb_at stamp is the durable dedupe; this set is the
   * cheap one in front of it, so a LOAD MORE or a re-render never spends a
   * round trip re-asking what this session already asked. Deliberately never
   * cleared on a new question: the answer is a fact about the GAME, not about
   * the browse that surfaced it.
   */
  const seatAsked = new Set<number>();

  /**
   * Restart the browse for whatever the question is now.
   *
   * Every entry point funnels through here — picking a tag, clearing one,
   * typing — so there is exactly one place that resets paging and exactly one
   * debounce. `live` is cleared immediately rather than on arrival, because
   * leaving the previous query's rows on screen under a new query's heading is
   * the kind of small lie this screen has been repeatedly guilty of.
   */
  const restart = (): void => {
    if (debT) clearTimeout(debT);
    const s = get();
    if (!browseMode(s)) {
      set({ live: [], bTotal: 0, bLocalN: 0, bLocalExtras: [], bStart: 0, bCapped: false, bState: '', bLoaded: 0 });
      // Clearing the last tag drops back to the DEFAULT view, which is now a
      // server-ranked feed rather than a client-side render of rows already
      // held. Without this the screen would return to whatever the feed had
      // before the browse started, or to nothing at all on a session that
      // opened straight into a tag.
      get().restartFeed();
      return;
    }
    set({ live: [], bTotal: 0, bLocalN: 0, bLocalExtras: [], bStart: 0, bCapped: false, bState: 'busy', bLoaded: 0 });
    debT = setTimeout(() => {
      void get().fetchBrowse();
    }, BROWSE_DEBOUNCE_MS);
  };

  return {
    bTagsBy: emptyTagMap(),
    xTagsBy: emptyTagMap(),
    bTotal: 0,
    bLocalN: 0,
    bLocalExtras: [],
    bStart: 0,
    bCapped: false,
    bState: '',
    bLoaded: 0,
    tagVocab: [],
    tagQ: '',

    /**
     * All three tag actions write to the CURRENT screen's lists and then
     * dispatch only if that screen is Discover. Off Discover there is nothing
     * to re-ask: the tags are a local predicate, so the selector recomputing
     * over them IS the whole update, and `discoverShown` is not this screen's
     * window to reset.
     *
     * On Discover, which pipeline restarts is decided by ONE question: did the
     * INCLUDE list change? The browse question is `q` + the includes and
     * nothing else, so an include change funnels through `restart()` (which
     * itself falls back to `restartFeed()` when browse mode just ended), while
     * an excludes-only change calls `restartFeed()` directly — a real refetch
     * in feed mode, where the exclude is part of the server question, and a
     * deliberate no-op in browse mode (`restartFeed` returns early there),
     * where recomputing over the excludes is the whole re-render.
     */
    cycleTag: (id) => {
      const key = fltKeyOf(get());
      const onDiscover = key === 'discover';
      // `discoverShown` is a new-question reset and only Discover has one, the
      // same way `setQ` resets it for a new query.
      const win = onDiscover ? { discoverShown: DISCOVER_LIMIT } : {};
      const s = get();
      if (s.bTagsBy[key].includes(id)) {
        // include → exclude. One atomic set: the id must never sit in both
        // lists, even for the duration of a render.
        set((st) => ({
          bTagsBy: { ...st.bTagsBy, [key]: st.bTagsBy[key].filter((t) => t !== id) },
          xTagsBy: { ...st.xTagsBy, [key]: [...st.xTagsBy[key], id] },
          ...win,
        }));
        if (onDiscover) restart();
      } else if (s.xTagsBy[key].includes(id)) {
        // exclude → off.
        set((st) => ({
          xTagsBy: { ...st.xTagsBy, [key]: st.xTagsBy[key].filter((t) => t !== id) },
          ...win,
        }));
        if (onDiscover) get().restartFeed();
      } else {
        // off → include.
        set((st) => ({ bTagsBy: { ...st.bTagsBy, [key]: [...st.bTagsBy[key], id] }, ...win }));
        if (onDiscover) restart();
      }
    },

    removeTag: (id) => {
      const key = fltKeyOf(get());
      const onDiscover = key === 'discover';
      const win = onDiscover ? { discoverShown: DISCOVER_LIMIT } : {};
      if (get().bTagsBy[key].includes(id)) {
        set((st) => ({
          bTagsBy: { ...st.bTagsBy, [key]: st.bTagsBy[key].filter((t) => t !== id) },
          ...win,
        }));
        if (onDiscover) restart();
      } else if (get().xTagsBy[key].includes(id)) {
        set((st) => ({
          xTagsBy: { ...st.xTagsBy, [key]: st.xTagsBy[key].filter((t) => t !== id) },
          ...win,
        }));
        if (onDiscover) get().restartFeed();
      }
    },

    clearTags: () => {
      const key = fltKeyOf(get());
      const onDiscover = key === 'discover';
      const hadInc = get().bTagsBy[key].length > 0;
      set((st) => ({
        bTagsBy: { ...st.bTagsBy, [key]: [] },
        xTagsBy: { ...st.xTagsBy, [key]: [] },
        ...(onDiscover ? { discoverShown: DISCOVER_LIMIT } : {}),
      }));
      if (!onDiscover) return;
      if (hadInc) restart();
      else get().restartFeed();
    },

    // Sort and filters are part of the question in BOTH modes now, so the
    // dispatch is the whole implementation: browse re-asks Steam (debounced by
    // `restart`, which a price slider needs as much as typing does), and the
    // feed re-asks `discover_page` exactly as before.
    restartDiscover: (prev) => {
      const s = get();
      if (!browseMode(s)) {
        get().restartFeed();
        return;
      }
      // Browse re-asks Steam only when the question STEAM was asked changed —
      // the sort, the price cap, the discount flag, coming-soon. A crew-lens,
      // demo or exact-discount-percentage change alters nothing upstream, so
      // the page in hand is still the right answer and `passFlt` narrowing it
      // at render is the entire update. Refetching on those would discard a
      // valid page to ask the identical question again, and blank the grid for
      // a debounce plus a round trip while doing it — `browseKey`'s existing
      // argument about `xTags`, applied to the filters that stayed local.
      //
      // A mutation that ENTERS browse mode (`prev` was not in it) always
      // differs, because `browseKey` folds in the term and the tags.
      if (browseKey(prev) !== browseKey(s)) restart();
    },

    // The picker has no opener of its own any more — it is the last section of
    // the filter panel, so `toggleFltOpen` resets this box and pulls the
    // vocabulary (`slices/ui.ts`).
    setTagQ: (tagQ) => set({ tagQ }),

    loadTagVocab: async () => {
      if (get().tagVocab.length > 0) return;
      const tags = await get().repo.fetchTagList();
      // An empty vocabulary is not an error worth a toast — it disables the
      // picker's search and leaves the popular list empty, which is visible
      // enough on its own.
      if (tags.length > 0) set({ tagVocab: tags });
    },

    fetchBrowse: async (opts) => {
      const more = opts?.more === true;
      const s0 = get();
      if (!browseMode(s0)) return;

      const keyAtDispatch = browseKey(s0);
      const start = more ? s0.bStart : 0;
      set({ bState: 'busy' });

      const term = s0.q.trim();
      // THE LOCAL COMPANION FETCH. Steam's search omits a delisted title
      // outright — verified live: 'rocket league' returns 45 fuzzy matches and
      // not Rocket League, while `discover_page` answers the same `q` with the
      // real row — so a term browse asks the games table the SAME question,
      // concurrently with Steam's page 0. Once per query, not per page: LOAD
      // MORE extends Steam's side only; the extras are already on screen.
      //
      // Term browses only. A tag-only browse is a question in Steam's tag-id
      // vocabulary, which no catalog column can answer (`steamApi.ts`: the two
      // tag namespaces must never be compared). When a term AND a tag are both
      // set the term is still asked locally — a catalog row's Steam-tag
      // membership is unknowable here, and unknown never excludes (DISCOVERY
      // decision 5).
      //
      // THE COMPANION STAYS UNFILTERED AND UNSORTED, which is now the opposite
      // of what rides to Steam beside it, so it is worth being exact about why.
      //
      // Sort: `discover_page` is asked for `defaultFilters()` and the session
      // sort would re-rank its handful of rows on a key Steam's page was not
      // ordered on. These rows LEAD the merged page by position — they are the
      // delisted-title door, the one thing Steam's search cannot answer — and
      // that position is the whole point of them. Ordering within a
      // twelve-row lead is not worth making the merge order conditional.
      //
      // Filters: the excludes and the client-side tri-states are applied at
      // RENDER by `discoverVM`, over every loaded row. A companion fetched with
      // filters already applied would have withheld rows server-side, which
      // then vanish from `hiddenByFlt`'s per-loaded count and stay missing after
      // the filter lifts — the count would describe a set the rows did not come
      // from, §5.5 again. Companion rows also carry no `tids`, so they are
      // tag-membership-unknown client-side and pass every exclude (decision 5),
      // which is the honest reading of a row Steam was never asked about.
      const [page, local] = await Promise.all([
        s0.repo.fetchBrowse({ term, tags: [...s0.bTagsBy.discover], start, ...browseNarrow(s0) }),
        !more && term !== ''
          ? s0.repo
              .fetchDiscoverPage({
                q: term,
                seed: s0.feedSeed,
                limit: BROWSE_PAGE,
                cursor: null,
                sort: s0.sort,
                flt: defaultFilters(),
                excludes: [],
              })
              // Both implementations answer failure with an empty page rather
              // than a throw, but a companion lookup must never be able to
              // take Steam's answer down with it.
              .catch(() => null)
          : Promise.resolve(null),
      ]);

      // The user moved on while this was in the air. Applying it now would show
      // one question's answer under another question's heading.
      if (browseKey(get()) !== keyAtDispatch) return;

      // A placeholder mint and a previously-registered live row both live in
      // the local pool without being local KNOWLEDGE — promoting either ahead
      // of Steam's own copy would lead the page with a thin row.
      const localRows = (local?.rows ?? []).filter(
        (g) => !g.live && !g.n.startsWith('Steam App #'),
      );
      const steamIds = new Set(page.items.map((g) => g.appid));
      // Rows only our ecosystem knows about. A row in BOTH sets is not an
      // extra: Steam's total already counts it, and the merge shows it once.
      // The appids, not just a count — a later page can prove one of these was
      // in Steam's total all along, and only the identity lets us un-count it.
      const localExtraIds = localRows
        .filter((g) => !steamIds.has(g.appid))
        .map((g) => g.appid);

      const steamDead = !page.fromNetwork && page.items.length === 0;
      if (steamDead && localRows.length === 0) {
        set({ bState: 'err' });
        get().freshFail('UPSTREAM_STATUS');
        return;
      }

      set((s) => {
        // THE CATALOG TWIN SWAP. A Steam row the catalog already knows renders
        // as its rich local row — real player counts, Steam display tags, a
        // rating band — IN STEAM'S SLOT, because the client never re-sorts a
        // server page. Two guards keep the word "rich" honest: a
        // `Steam App #<appid>` mint has no name worth showing, and a live twin
        // is just an older copy of the same thin row. `prov` is deliberately
        // NOT a guard: 942 of 950 browsable rows are pool mints, i.e. the very
        // population this swap exists for, and rejecting them left it firing
        // for the 8 curated seed rows alone. The fear a prov guard answered —
        // schema-default `mn`/`mx` = 1 faking a SOLO claim — applies to no row
        // the database can now contain: pool mints and `ensure_game` have
        // written the 0/0 unknown sentinel since migrations 2400/2500.
        //
        // The review carry (`rp` and, since 3040, Steam's rung `rs`) is
        // display-only client state — nothing here writes back to the server
        // (decision D2) — but it must land in BOTH copies: `gById` resolves
        // catalog-first, so patching only the `live` copy shows a rating band on
        // the tile and an em dash on the Detail screen it opens, and no
        // SteamFactPatch producer ever heals either field.
        const carried = new Map<number, Game>();
        // THE SESSION-FACT OVERLAY, for the rows the swap leaves as Steam's —
        // AND ONLY OVER THE PRICE-SHAPED FIELDS.
        //
        // Why it exists: `toBrowseGame` hard-codes `p: null, o: 0, rd: ''` — a
        // page cached for ten minutes has no business quoting a price — so a
        // game whose price and discount a Detail open healed a minute ago came
        // back as TBA the moment it reappeared under a new query, and every LOAD
        // MORE re-thinned it. `searchLive` has fixed exactly this for exactly
        // this array since `withKnownFacts`; browse rows were never given the
        // same pass. Those three fields are the whole of the defect, and they
        // are precisely the ones a browse row structurally cannot carry.
        //
        // WHY up / ea / demo / dAid ARE EXCLUDED, which is the opposite of what
        // this comment used to claim. It argued that `applyFact` copying DEFINED
        // fields only made an erased ribbon impossible. It does not:
        // `mapAppDetails` sets `up`, `ea` and `demo` UNCONDITIONALLY on every
        // patch it builds — `out.demo = demos.length > 0` is `false` for any
        // appdetails response without a `demos` array, and `out.ea` is a
        // genre-70 / "early access" text inference — so a stored patch always
        // has all three defined and applying it always overwrites.
        //
        // That would be harmless if the two sources agreed, and they do not
        // agree STRUCTURALLY. A browse row's flags came off GetItems on THIS
        // request, where a demo is `related_items.demo_appid` and Early Access
        // is the `is_early_access` flag; appdetails answers the same two
        // questions from `demos[]` and from a genre list. One of them saying
        // nothing is routine and is not the other one saying no. So a Detail
        // open on Tuesday could erase, on Friday's browse, a DEMO ribbon Steam
        // had just confirmed in the same response that drew the tile.
        //
        // The row's own GetItems flags therefore win, unconditionally. This is
        // the same rule `worker/src/steam.ts#ItemFlags` and migration 2903 both
        // state from the other end — appdetails owns the negative, GetItems owns
        // the positive — applied here to which half of a patch may be replayed.
        const heal = (g: Game): Game => {
          const f = s.facts[String(g.appid)];
          if (!f) return g;
          const priced: SteamFactPatch = { appid: g.appid };
          if (f.p !== undefined) priced.p = f.p;
          if (f.o !== undefined) priced.o = f.o;
          if (f.rd !== undefined) priced.rd = f.rd;
          return applyFact(g, priced);
        };
        // The browse row's `tids` stay on the LIVE copy of a swapped twin —
        // Steam just said which tags the row carries, and losing that to the
        // swap would let a rich row dodge an exclude its thin neighbours obey.
        // The `carried` catalog patch stays clean: `tids` is browse-surface
        // data, and the live/catalog copies may now differ by this one
        // render-inert field — the agreement that mattered (`rp`, tile vs
        // Detail) is agreement of VALUE, and still holds.
        const withTids = (t: Game, src: Game): Game =>
          src.tids?.length ? { ...t, tids: src.tids } : t;
        const swap = (g: Game): Game => {
          const twin = s.catalog.find((c) => c.appid === g.appid);
          if (!twin || twin.live || twin.n.startsWith('Steam App #')) return heal(g);
          // The browse row rode in with Steam's own review percentage AND its
          // rung; most pool-minted twins have neither until the nightly pool
          // reaches them.
          //
          // THE RUNG CARRIES SEPARATELY, and it has to. `rungOf` falls back to
          // the percentage only where the percentage is decisive, and 80%+ is
          // exactly where it is not — three rungs are possible and only a review
          // count can choose. So a twin that took `rp: 96` from the browse row
          // and left `rs` behind would render a bare `96%` with no label at all,
          // on the population this swap exists for (942 of 950 browsable rows
          // are pool mints). Two independent tests, not one: a twin can have a
          // percentage from an earlier pass and still be missing the rung 3040
          // added.
          const takeRp = twin.rp === 0 && g.rp > 0;
          const takeRs = twin.rs === undefined && g.rs !== undefined;
          if (takeRp || takeRs) {
            const rich: Game = { ...twin };
            if (takeRp) rich.rp = g.rp;
            if (g.rs !== undefined && takeRs) rich.rs = g.rs;
            carried.set(rich.appid, rich);
            return withTids(rich, g);
          }
          return withTids(twin, g);
        };
        // Append on LOAD MORE, replace on a new question. Deduped by appid:
        // Steam's paging is not a stable snapshot, so the same game can appear
        // on two pages, and a duplicate React key is a real bug not a cosmetic
        // one. Page 0 seeds the seen-set with the local rows that LEAD the
        // page — they are the games our ecosystem already knows, so Steam's
        // copy of one is the duplicate, never the other way around. Later
        // pages dedupe against everything shown, local extras included.
        const seen = new Set((more ? s.live : localRows).map((g) => g.appid));
        const fresh = page.items.filter((g) => !seen.has(g.appid)).map(swap);
        const live = more ? [...s.live, ...fresh] : [...localRows, ...fresh];
        // The extras self-correct. Page 0 counted an extra against page 0's
        // Steam rows only, so an extra that Steam's total counts but ranks
        // below the first page is counted twice — and stays that way unless a
        // later page's arrival of Steam's copy (deduped out of `live` above)
        // also removes it here. `bTotal` is recomputed from `page.total` every
        // page, so dropping the appid is what makes the match line converge on
        // the truth instead of holding LOAD MORE open forever.
        let bLocalExtras = more ? s.bLocalExtras : localExtraIds;
        if (more && bLocalExtras.some((a) => steamIds.has(a))) {
          bLocalExtras = bLocalExtras.filter((a) => !steamIds.has(a));
        }
        const bLocalN = bLocalExtras.length;
        return {
          live,
          // The review-carried twins, patched into the catalog as the identical
          // objects the page shows — display-only client state, no server
          // write — so tile and Detail agree on the rating AND its rung.
          catalog: carried.size
            ? s.catalog.map((c) => carried.get(c.appid) ?? c)
            : s.catalog,
          bLocalN,
          bLocalExtras,
          bLoaded: live.length,
          // Steam's count of everything matching, plus what only we could
          // answer. §5.5 caveat, accepted: a local extra Steam counts but has
          // not yet paged out IS counted twice until the page carrying Steam's
          // copy lands — that arrival is also the first evidence Steam counts
          // it at all — and the alternative, not counting extras, makes the
          // line lie about the one case this merge exists for.
          bTotal: page.total + bLocalN,
          bStart: start + BROWSE_PAGE,
          bCapped: page.capped,
          // Local matches under a dead Steam search still render — with the
          // error state kept, so the match line says STEAM SEARCH OFFLINE over
          // the rows we could answer from instead of pretending either way.
          bState: steamDead ? 'err' : 'ok',
          // Art came back WITH the rows, so nothing re-fetches it. `artCands`
          // prefers a fetched asset over a guessed legacy URL, which for a
          // modern appid is the difference between art and a blank tile. The
          // local page's art lands last: where both sides answered, the db art
          // wins; where only Steam did, the browse art stays — which is also
          // what keeps a swapped twin without art from going blank.
          assets: { ...s.assets, ...page.assets, ...(local?.assets ?? {}) },
        };
      });
      if (page.fromNetwork) get().freshOk();
      else if (steamDead) get().freshFail('UPSTREAM_STATUS');

      // THE SEAT HEAL, fired after the page is on screen — never awaited, so
      // the tiles paint first and the counts pop in a beat later, exactly the
      // price heal's rhythm. Over the WHOLE array rather than this page's
      // fresh rows: `seatAsked` makes the difference free, and it also catches
      // a companion row whose pool entry has no count yet. Three exclusions,
      // each an ask that could not change the screen:
      //   mx > 0            the row already knows (a swapped twin's resolved
      //                     count — override, editorial, IGDB, whatever won);
      //   soloOnlyCats      Steam positively said single-player-only, which
      //                     the lens already acts on without a number — and
      //                     which the SERVER could not know for an orphan,
      //                     since the labels derivation is pool-scoped;
      //   appid <= 0        a CheapShark id has nothing to look up.
      const seatWants = get()
        .live.filter((g) => g.appid > 0 && !(g.mx > 0) && !soloOnlyCats(g.pcats))
        .map((g) => g.appid);
      void get().lookupSeats(seatWants);

      // THE OWNED-BY HEAL, same rhythm and the same reason as the seat heal
      // above. A Steam browse is the one Discover mode whose rows are neither
      // in the catalog nor filtered server-side: the feed's ownership AND runs
      // inside `discover_page` (3047), the boards' runs over `catalog`, and
      // these rows are in neither population. Without this the section would
      // sit active on the panel and quietly pass every Steam result, because
      // `passFlt` reads an appid it has no answer for as UNKNOWN and keeps it —
      // which is right, and is exactly why somebody has to ask.
      //
      // Gated on the filter being ON. Asking about 50 arbitrary Steam appids on
      // every browse page would spend a round trip per page to answer a
      // question nobody put.
      if (get().fltBy.discover.own.length > 0) {
        get().ensureOwnedBy(get().live.map((g) => g.appid));
      }
    },

    lookupSeats: async (appids) => {
      const want = appids.filter((a) => !seatAsked.has(a));
      if (want.length === 0) return;
      for (const a of want) seatAsked.add(a);

      // ≤50 per call (the function's page-shaped cap); sequential chunks on
      // the rare page-0 merge that exceeds it.
      const counts: SeatCount[] = [];
      for (let i = 0; i < want.length; i += 50) {
        try {
          counts.push(...(await get().repo.lookupSeats(want.slice(i, i + 50))));
        } catch {
          // The repository contract is fail-soft ([]), so a throw here is a
          // bug on its side — but an enrichment must still never take the
          // screen down with it. The asked-set keeps the appids; the igdb_at
          // stamp server-side is the durable state either way.
          break;
        }
      }
      if (counts.length === 0) return;

      const byId = new Map(counts.map((c) => [c.appid, c]));
      set((s) => ({
        // Patch by appid over whatever is on screen NOW — a stale-response
        // guard would be wrong here, not just unnecessary: a seat count is a
        // fact about the GAME, not about the browse that surfaced it, so a
        // page that changed under the ask still deserves any answers that
        // apply to it. FILL-ONLY: a row that meanwhile learned its count
        // (a swap, another heal) is not touched.
        live: s.live.map((g) => {
          const c = byId.get(g.appid);
          if (!c || g.mx > 0) return g;
          const out: Game = { ...g, mn: c.mn, mx: c.mx };
          if (c.unb) out.punb = true;
          return out;
        }),
      }));
    },
  };
};
